import * as React from 'react';
import { cn } from '@cguard/design-system';
import { useTranslation } from 'react-i18next';
import {
  deleteCentralWhepSession,
  deleteWhepResource,
  exchangeCentralWhepOffer,
  exchangeWhepOffer,
  WhepError,
} from '@cguard/api-client';
import {
  FRAME_STALL_AFTER_MS,
  readTotalVideoFrames,
  subscribeFrameHealth,
  type FrameHealth,
  type VideoFrameCallbackVideoElement,
} from './frame-health';
import { VideoStateOverlay } from './video-state-overlay';

/**
 * `<WhepVideoPlayer/>` — WebRTC live-video tile fed by a WHEP endpoint.
 *
 * Day 7 release-gate component (sprint
 * `sprint-2026-05-21-admin-console-reconciliation.md`, item G4). Replaces
 * the long-standing `NO STREAM` placeholder on the operator-console camera
 * grid with an actual, low-latency H.264 stream from the
 * MediaMTX (central) / go2rtc (edge) WHEP endpoint.
 *
 * <h3>Why WHEP for live and HLS for review</h3>
 *
 * Per memory `feedback_streaming_architecture`, V1 ships a three-tier
 * transport split:
 *
 * - **Tier A — WHEP / WebRTC**: live tiles. Sub-second glass-to-glass
 *   when the device is the same LAN as the edge; ~1–2 s glass-to-glass
 *   through MediaMTX central. This is the contract the camera grid is
 *   pinned to.
 * - **Tier B — LL-HLS**: 2–4 s glass-to-glass, used for the camera
 *   detail page + admin-console preview tiles where the WebRTC handshake
 *   cost (~300–600 ms ICE) is wasted. See `HlsVideoPlayer` for that
 *   path.
 * - **Tier C — fMP4 playback**: forensic / review, served from the
 *   recording bucket via signed URLs.
 *
 * <h3>WHEP protocol summary</h3>
 *
 * Per IETF draft-ietf-wish-whep:
 *
 * 1. Client `POST <whep-url>` with `Content-Type: application/sdp` and
 *    body = local SDP offer.
 * 2. Server responds `201 Created` with `Content-Type: application/sdp`
 *    and body = SDP answer. `Location` header carries the resource URL
 *    for `DELETE` (graceful teardown).
 *
 * Both MediaMTX (≥ 1.5) and go2rtc (≥ 1.9) implement this. The
 * `tools/edge-debug-viewer/index.html` smoke surface uses the same
 * pattern against go2rtc; this component is the productised version
 * with proper React lifecycle, authenticated central signalling, and a
 * state machine.
 *
 * <h3>Codec lock — H.264 only in V1</h3>
 *
 * Per memory `project_h265_default_assumption`, the edge transcodes the
 * camera-sub stream from H.265 → H.264 before WHEP egress. The browser
 * therefore only ever sees H.264 SDP. We add no client-side codec
 * enforcement here (unlike `HlsVideoPlayer`, which inspects manifest
 * levels): if the SDP offered by MediaMTX advertises HEVC the
 * `RTCPeerConnection.setRemoteDescription` will fail and we surface that
 * as a stream error — but the codec contract is enforced server-side,
 * not in this component.
 *
 * <h3>Autoplay policy</h3>
 *
 * Chromium / Safari / Firefox all block `<video>` autoplay-unmuted on
 * first paint. We therefore render the element `muted={true}` and let
 * the parent grid surface an Unmute control on the camera tile chrome.
 * Trying to coax `play()` into accepting unmuted audio without a user
 * gesture is futile; the spec says no.
 *
 * <h3>State machine</h3>
 *
 * - `connecting` — peer connection created; ICE gathering; SDP exchange
 *   in flight. Shown with a "Connecting…" spinner overlay.
 * - `playing` — `ontrack` fired and the `<video>` is actually drawing
 *   frames (we listen to `playing` and `timeupdate` to confirm).
 * - `error` — anything went wrong. Surface message + a Retry button
 *   that bumps the `retryToken` and restarts the effect from scratch.
 *
 * <h3>Cleanup</h3>
 *
 * On unmount or session-mode change we (a) abort the
 * in-flight `fetch`, (b) issue a best-effort `DELETE` using either the
 * opaque central session handle or the explicit demo resource URL, and
 * (c) close the `RTCPeerConnection`. Without (b), MediaMTX would
 * keep the consumer slot open until its idle-timeout; on a busy grid
 * that's enough to exhaust the per-stream consumer quota during rapid
 * layout flips (1-up → 16-up → 1-up).
 */
export interface WhepVideoPlayerProps {
  /**
   * Logical camera id. Central mode sends it to the Cameras proxy. Direct
   * demo mode also uses it as the default WHEP stream path segment, which can
   * be overridden via `streamName` or the VITE_WHEP stream mapping variables.
   */
  cameraId: string;
  /** Human-readable camera name for the `<video>`'s accessible label. */
  cameraName: string;
  /**
   * WHEP endpoint base URL. Direct MediaMTX origins use
   * `https://staging-mediamtx.taild8e24c.ts.net/{stream}/whep`; legacy
   * reverse proxies may expose `/whep/{stream}`. go2rtc exposes
   * `/api/webrtc?src={stream}`. `resolveWhepConfig` supports these
   * shapes from a single base URL.
   * Supplying this prop explicitly selects the unauthenticated direct mode
   * retained for demo lanes. Authenticated consoles omit it and use the
   * central session proxy through `getAccessToken`.
   */
  whepBaseUrl?: string;
  /** Override the stream path segment if your MediaMTX `paths` mapping differs from cameraId-equals-stream. */
  streamName?: string;
  /**
   * Desired stream profile. Dense grids pass `sub`; the player then uses
   * VITE_WHEP_SUB_STREAM_MAP / VITE_WHEP_SUB_STREAM_SUFFIX when explicitly
   * configured. It falls back to the main WHEP map rather than guessing a
   * low-bandwidth path, because boxes must not be drawn on unrelated pixels.
   *
   * The receive-side jitter buffer defaults also key off this variant:
   * `sub` tiles are dense-grid playback and get extra playout cushion, while
   * `main` stays as low-latency as the browser allows.
   */
  streamVariant?: 'main' | 'sub';
  /** Optional className applied to the outer wrapper (sizing). */
  className?: string;
  /** Browser autoplay policies require muted=true; default `true`. */
  muted?: boolean;
  /** Default `true` so the grid plays as soon as ICE settles. */
  autoplay?: boolean;
  /** Supplies a fresh bearer token for each central offer and teardown request. */
  getAccessToken?: AccessTokenSupplier;
  /**
   * Optional client-side handshake delay. Dense camera walls use this to stagger
   * WHEP POST/ICE startup so a 16-up grid does not stampede the browser and
   * MediaMTX with every peer connection in the same render frame.
   */
  connectDelayMs?: number;
  /**
   * Optional receive-side jitter-buffer target, in milliseconds. Leave unset
   * to use the stream-variant default, or pass `null` to avoid setting a target
   * even for grid/substream tiles.
   */
  jitterBufferTargetMs?: number | null;
  /**
   * Optional access to the live <video> element. Consumers use this for
   * read-only frame sampling (for example, local face thumbnails) without
   * reaching through the DOM.
   */
  onVideoElement?: (video: HTMLVideoElement | null) => void;
  /**
   * Optional status slot driven by this player's handshake and decoded-frame
   * health. Use it for per-camera transport chrome instead of a separate SSE
   * or camera-health signal.
   */
  renderTransportStatus?: (status: WhepTransportStatus) => React.ReactNode;
  /** Lets a composed player advance to its next transport without an error tile. */
  onUnavailable?: () => void;
  onTransportStatusChange?: (status: WhepTransportStatus) => void;
  /** Overlay slot — typically the `<DetectionOverlay/>` SVG. */
  children?: React.ReactNode;
}

type PlayerState = 'connecting' | 'playing' | 'error';
type ErrorKind = 'config' | 'network' | 'sdp' | 'stream';
export type WhepTransportStatus = 'connecting' | 'live' | 'offline';

const INTERRUPTED_PLAY_RETRY_LIMIT = 3;
const INTERRUPTED_PLAY_RETRY_MS = 120;
const INTERRUPTED_PLAY_BACKOFF_MS = 1_000;
const ICE_GATHERING_SOFT_TIMEOUT_MS = 350;
const GRID_RECEIVER_JITTER_BUFFER_TARGET_MS = 180;
const MAX_RECEIVER_JITTER_BUFFER_TARGET_MS = 4_000;

interface ResolvedConfig {
  readonly url: string;
  readonly streamSegment: string;
}

export type AccessTokenSupplier = () => string | null | Promise<string | null>;

type ResolvedSessionMode =
  | { readonly kind: 'central' }
  | { readonly kind: 'direct'; readonly config: ResolvedConfig };

export function resolveReceiverJitterBufferTargetMs({
  streamVariant = 'main',
  jitterBufferTargetMs,
}: {
  readonly streamVariant?: 'main' | 'sub';
  readonly jitterBufferTargetMs?: number | null;
}): number | null {
  const rawTarget =
    jitterBufferTargetMs !== undefined
      ? jitterBufferTargetMs
      : streamVariant === 'sub'
        ? GRID_RECEIVER_JITTER_BUFFER_TARGET_MS
        : null;
  if (rawTarget === null || !Number.isFinite(rawTarget)) return null;
  return Math.min(MAX_RECEIVER_JITTER_BUFFER_TARGET_MS, Math.max(0, Math.trunc(rawTarget)));
}

/**
 * Resolve the WHEP URL the player should hit. Pure so callers (and the
 * future runtime-config indirection) can unit-test it.
 *
 * <p>Returns null when nothing is configured — the player then renders
 * `error / config` rather than firing a relative-URL fetch (which would
 * produce a confusing "Network error" overlay).</p>
 */
export function resolveWhepConfig(props: {
  cameraId: string;
  streamName?: string;
  streamVariant?: 'main' | 'sub';
  whepBaseUrl?: string;
}): ResolvedConfig | null {
  const rawBase = props.whepBaseUrl ?? '';
  const base = rawBase.trim().replace(/\/+$/, '');
  if (!base) return null;
  const segment = resolveWhepStreamSegment(props);
  if (!segment) return null;
  // Lazy: do not URL-encode the stream segment — MediaMTX path names are
  // restricted to `[A-Za-z0-9._-]` server-side for our deployments, so the
  // configured stream string is already URL-safe.
  const url =
    resolveGo2rtcWebrtcUrl(base, segment) ??
    (base.endsWith('/whep') ? `${base}/${segment}` : `${base}/${segment}/whep`);
  return { url, streamSegment: segment };
}

function resolveGo2rtcWebrtcUrl(base: string, segment: string): string | null {
  try {
    const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(base);
    const url = new URL(base, isAbsolute ? undefined : 'http://cguard.local');
    const path = url.pathname.replace(/\/+$/, '');
    if (!path.endsWith('/api/webrtc')) return null;
    url.pathname = path;
    url.searchParams.set('src', segment);
    if (isAbsolute) return url.toString();
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

function resolveWhepStreamSegment(props: {
  cameraId: string;
  streamName?: string;
  streamVariant?: 'main' | 'sub';
}): string {
  const explicit = props.streamName?.trim();
  if (explicit) return normalizeWhepStreamSegment(explicit);

  const cameraId = props.cameraId.trim();
  const streamVariant = props.streamVariant ?? 'main';
  if (streamVariant === 'sub') {
    const subMapped = resolveMappedWhepStreamSegment(cameraId, 'VITE_WHEP_SUB_STREAM_MAP');
    if (subMapped) return subMapped;

    const subSuffix = (
      (import.meta.env.VITE_WHEP_SUB_STREAM_SUFFIX as string | undefined) ?? ''
    ).trim();
    if (cameraId && subSuffix && !cameraId.endsWith(subSuffix)) {
      const suffixed = normalizeWhepStreamSegment(`${cameraId}${subSuffix}`);
      if (suffixed) return suffixed;
    }
  }

  const mapped = resolveMappedWhepStreamSegment(cameraId, 'VITE_WHEP_STREAM_MAP');
  if (mapped) return mapped;

  const envStreamName = (
    (import.meta.env.VITE_WHEP_STREAM_NAME as string | undefined) ?? ''
  ).trim();
  if (envStreamName) return normalizeWhepStreamSegment(envStreamName);

  const suffix = ((import.meta.env.VITE_WHEP_STREAM_SUFFIX as string | undefined) ?? '').trim();
  if (cameraId && suffix && !cameraId.endsWith(suffix)) {
    const suffixed = normalizeWhepStreamSegment(`${cameraId}${suffix}`);
    if (suffixed) return suffixed;
  }
  return normalizeWhepStreamSegment(cameraId);
}

function resolveMappedWhepStreamSegment(cameraId: string, envName: string): string {
  if (!cameraId) return '';
  const rawEnv = (import.meta.env as Record<string, string | undefined>)[envName];
  const raw = (rawEnv ?? '').trim();
  if (!raw) return '';

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return '';
    const value = (parsed as Record<string, unknown>)[cameraId];
    if (typeof value !== 'string') return '';
    const segment = value.trim();
    return normalizeWhepStreamSegment(segment);
  } catch {
    return '';
  }
}

function normalizeWhepStreamSegment(segment: string): string {
  if (!/^[A-Za-z0-9._-]+$/.test(segment)) return '';
  return segment.endsWith('-ai') ? '' : segment;
}

function isInterruptedPlayRequest(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const name = Reflect.get(err, 'name');
  const message = Reflect.get(err, 'message');
  return (
    name === 'AbortError' ||
    (typeof message === 'string' && /interrupted by a new load request/i.test(message))
  );
}

async function requireAccessToken(supplier: AccessTokenSupplier | undefined): Promise<string> {
  const token = (await supplier?.())?.trim() ?? '';
  if (!token) {
    throw new WhepError('config', 'Central WHEP signalling requires an access token.');
  }
  return token;
}

export function WhepVideoPlayer({
  cameraId,
  cameraName,
  whepBaseUrl,
  streamName,
  streamVariant = 'main',
  className,
  muted = true,
  autoplay = true,
  getAccessToken,
  connectDelayMs = 0,
  jitterBufferTargetMs,
  onVideoElement,
  renderTransportStatus,
  onUnavailable,
  onTransportStatusChange,
  children,
}: WhepVideoPlayerProps): React.JSX.Element {
  // Aliased to `tr` to avoid shadowing the inner `track` and other
  // `t`-named bindings the WebRTC effect uses (oxlint no-shadow).
  const { t: tr } = useTranslation('common');
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [state, setState] = React.useState<PlayerState>('connecting');
  const [frameHealth, setFrameHealth] = React.useState<FrameHealth>('waiting');
  const [errorKind, setErrorKind] = React.useState<ErrorKind | null>(null);
  const [retryToken, setRetryToken] = React.useState(0);
  const handleVideoRef = React.useCallback(
    (video: HTMLVideoElement | null): void => {
      videoRef.current = video;
      onVideoElement?.(video);
    },
    [onVideoElement],
  );

  const sessionMode = React.useMemo<ResolvedSessionMode | null>(() => {
    if (whepBaseUrl?.trim()) {
      const config = resolveWhepConfig({
        cameraId,
        ...(streamName ? { streamName } : {}),
        streamVariant,
        whepBaseUrl,
      });
      return config ? { kind: 'direct', config } : null;
    }
    return getAccessToken ? { kind: 'central' } : null;
  }, [cameraId, getAccessToken, streamName, streamVariant, whepBaseUrl]);
  const connectDelay = Number.isFinite(connectDelayMs)
    ? Math.max(0, Math.trunc(connectDelayMs))
    : 0;
  const receiverJitterBufferTargetMs = resolveReceiverJitterBufferTargetMs({
    streamVariant,
    ...(jitterBufferTargetMs !== undefined ? { jitterBufferTargetMs } : {}),
  });

  React.useEffect(() => {
    if (!sessionMode) {
      setState('error');
      setFrameHealth('waiting');
      setErrorKind('config');
      return undefined;
    }

    const video = videoRef.current;
    if (!video) return undefined;

    setState('connecting');
    setFrameHealth('waiting');
    setErrorKind(null);

    const abortController = new AbortController();
    // Separate, longer-lived AbortController for the teardown `DELETE`.
    // The handshake `abortController` is fired during cleanup to cancel
    // the in-flight POST, but we need an independent signal we can hand
    // to `deleteWhepResource` so that rapid layout-flips (1-up → 16-up →
    // 1-up) can also cancel a pending DELETE if a later effect run for
    // the same tile supersedes this one. The signal is plumbed through
    // the cleanup path; this controller is never aborted by the effect
    // itself (the DELETE should complete or be page-unload-cancelled),
    // but is exposed so future call-sites can wire grid-level cancellation.
    const teardownAbortController = new AbortController();
    let cancelled = false;
    let sessionResource:
      | { readonly kind: 'central'; readonly handle: string }
      | { readonly kind: 'direct'; readonly url: string }
      | null = null;
    let playingConfirmed = false;
    let playRequested = false;
    let frameCallbackHandle: number | null = null;
    let unsubscribeFrameHealth: (() => void) | null = null;
    let lastFrameProgressAtMs = performance.now();
    let lastVideoFrameCount = -1;
    let lastCurrentTime = -1;
    let handshakeTimer: ReturnType<typeof setTimeout> | null = null;
    let playRetryTimer: ReturnType<typeof setTimeout> | null = null;
    let interruptedPlayRetries = 0;
    let connectionReleased = false;

    // Pin a single, dedicated PeerConnection per player effect. STUN/TURN
    // is intentionally empty — MediaMTX in the cluster + go2rtc on the
    // edge are reachable on the host-prefix-routable Tailscale net, which
    // is L3 reachable. If we extend to public-internet WHEP later, this is
    // where TURN creds plug in.
    let pc: RTCPeerConnection;
    try {
      pc = new RTCPeerConnection({ iceServers: [] });
    } catch {
      setState('error');
      setErrorKind('config');
      return undefined;
    }
    const videoTransceiver = pc.addTransceiver('video', { direction: 'recvonly' });
    pc.addTransceiver('audio', { direction: 'recvonly' });
    applyReceiverJitterBufferTarget(
      videoTransceiver?.receiver ?? null,
      receiverJitterBufferTargetMs,
    );

    const setFrameHealthIfChanged = (next: FrameHealth): void => {
      setFrameHealth((prev) => (prev === next ? prev : next));
    };

    const readVideoFrameCount = (): number | null => readTotalVideoFrames(video);

    const markFrameProgress = (): void => {
      if (cancelled) return;
      lastFrameProgressAtMs = performance.now();
      lastVideoFrameCount = readVideoFrameCount() ?? lastVideoFrameCount;
      lastCurrentTime = video.currentTime;
      setFrameHealthIfChanged('flowing');
    };

    const clearPlayRetryTimer = (): void => {
      if (playRetryTimer === null) return;
      clearTimeout(playRetryTimer);
      playRetryTimer = null;
    };

    const markPlaying = (): void => {
      if (cancelled) return;
      markFrameProgress();
      if (playingConfirmed) return;
      playingConfirmed = true;
      interruptedPlayRetries = 0;
      clearPlayRetryTimer();
      setState('playing');
    };

    const cancelFrameCallback = (): void => {
      if (frameCallbackHandle === null) return;
      const frameVideo = video as VideoFrameCallbackVideoElement;
      frameVideo.cancelVideoFrameCallback?.(frameCallbackHandle);
      frameCallbackHandle = null;
    };

    const stopFrameHealthSampling = (): void => {
      if (unsubscribeFrameHealth === null) return;
      unsubscribeFrameHealth();
      unsubscribeFrameHealth = null;
    };

    const releaseSession = (): void => {
      if (sessionResource?.kind === 'central') {
        const handle = sessionResource.handle;
        sessionResource = null;
        void (async (): Promise<void> => {
          try {
            const bearerToken = await requireAccessToken(getAccessToken);
            await deleteCentralWhepSession(handle, {
              bearerToken,
              signal: teardownAbortController.signal,
            });
          } catch {
            // Teardown is best-effort during unmount or failed negotiation.
          }
        })();
      } else if (sessionResource?.kind === 'direct') {
        void deleteWhepResource(sessionResource.url, teardownAbortController.signal).catch(() => {
          // Teardown is best-effort during unmount or failed negotiation.
        });
        sessionResource = null;
      }
    };

    const releaseConnection = (): void => {
      releaseSession();
      if (connectionReleased) return;
      connectionReleased = true;
      if (handshakeTimer !== null) {
        clearTimeout(handshakeTimer);
        handshakeTimer = null;
      }
      clearPlayRetryTimer();
      abortController.abort();
      cancelFrameCallback();
      stopFrameHealthSampling();
      if (video.srcObject) {
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {
            // Track may have already stopped; swallow.
          }
        });
        video.srcObject = null;
      }
      try {
        pc.close();
      } catch {
        // pc.close() can throw if called on an already-closed pc.
      }
    };

    // The raw failure is a diagnostic for the console; the overlay maps the
    // kind to a catalog sentence.
    const setTerminalError = (kind: ErrorKind, detail: string | null): void => {
      if (cancelled) return;
      if (detail) console.warn(`[WhepVideoPlayer] ${cameraId}:`, detail);
      releaseConnection();
      setState('error');
      setErrorKind(kind);
    };

    const armFirstFrameCallback = (): void => {
      if (playingConfirmed) return;
      const frameVideo = video as VideoFrameCallbackVideoElement;
      if (typeof frameVideo.requestVideoFrameCallback !== 'function') return;
      cancelFrameCallback();
      frameCallbackHandle = frameVideo.requestVideoFrameCallback(() => {
        frameCallbackHandle = null;
        markPlaying();
      });
    };

    const markPlayingFromMediaEvent = (): void => {
      if (cancelled) return;
      if (
        video.readyState >= video.HAVE_CURRENT_DATA &&
        (video.videoWidth > 0 || video.currentTime > 0)
      ) {
        markPlaying();
      }
    };

    const markFrameStalled = (): void => {
      if (cancelled) return;
      setFrameHealthIfChanged('stalled');
    };

    const sampleFrameHealth = (): void => {
      if (cancelled || connectionReleased || !playingConfirmed) return;
      const frameCount = readVideoFrameCount();
      if (frameCount !== null) {
        if (frameCount > lastVideoFrameCount) {
          markFrameProgress();
          return;
        }
      } else if (video.currentTime > lastCurrentTime + 0.01) {
        markFrameProgress();
        return;
      }

      if (performance.now() - lastFrameProgressAtMs > FRAME_STALL_AFTER_MS) {
        markFrameStalled();
      }
    };

    const startPlayback = (): void => {
      if (!autoplay || playRequested || playRetryTimer !== null) return;
      playRequested = true;
      let playPromise: Promise<void> | undefined;
      try {
        playPromise = video.play();
      } catch (err) {
        playRequested = false;
        if (handlePlaybackFailure(err)) return;
        setTerminalError('stream', err instanceof Error ? err.message : String(err));
        return;
      }
      if (playPromise === undefined) return;
      void playPromise.catch((err: unknown) => {
        if (cancelled || playingConfirmed || connectionReleased) return;
        playRequested = false;
        if (handlePlaybackFailure(err)) return;
        setTerminalError('stream', err instanceof Error ? err.message : String(err));
      });
    };

    const handlePlaybackFailure = (err: unknown): boolean => {
      if (cancelled || playingConfirmed || connectionReleased) return true;
      if (!isInterruptedPlayRequest(err) || video.srcObject === null) {
        return false;
      }

      const retryDelay =
        interruptedPlayRetries < INTERRUPTED_PLAY_RETRY_LIMIT
          ? INTERRUPTED_PLAY_RETRY_MS
          : INTERRUPTED_PLAY_BACKOFF_MS;
      interruptedPlayRetries = Math.min(interruptedPlayRetries + 1, INTERRUPTED_PLAY_RETRY_LIMIT);
      clearPlayRetryTimer();
      playRetryTimer = setTimeout(() => {
        playRetryTimer = null;
        startPlayback();
      }, retryDelay);
      return true;
    };

    pc.ontrack = (ev) => {
      if (cancelled) return;
      const stream = ev.streams[0];
      if (stream && video) {
        // Assign immediately so we paint as soon as the first decoded
        // frame is ready. `ontrack` only confirms negotiation, not that
        // bytes are actually decoded, so state flips only after the video
        // element presents a frame or fires a real media playback event.
        video.srcObject = stream;
        armFirstFrameCallback();
        startPlayback();
      }
    };

    pc.onconnectionstatechange = () => {
      if (cancelled || connectionReleased) return;
      const cs = pc.connectionState;
      // `failed` is the obvious terminal state. `closed` we also treat
      // as terminal-with-retry: the player can otherwise sit on
      // `state="connecting"` forever if the peer connection is closed
      // mid-handshake (race between cleanup and the SDP exchange, or a
      // server-side close before our `setRemoteDescription` resolves) —
      // the connecting overlay would then never flip. `disconnected`
      // sometimes recovers on its own so we don't surface it; a future
      // production-grade revision could debounce `disconnected → failed`.
      if (cs === 'failed' || cs === 'closed') {
        setTerminalError('network', `peer connection ${cs}`);
      }
    };

    video.addEventListener('playing', markPlayingFromMediaEvent);
    video.addEventListener('loadeddata', markPlayingFromMediaEvent);
    video.addEventListener('timeupdate', markPlayingFromMediaEvent);
    video.addEventListener('waiting', markFrameStalled);
    video.addEventListener('stalled', markFrameStalled);
    video.addEventListener('suspend', markFrameStalled);
    unsubscribeFrameHealth = subscribeFrameHealth(sampleFrameHealth);

    const handshake = async (): Promise<void> => {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // Wait for ICE gathering to complete so we POST a single SDP
        // with all candidates. MediaMTX's WHEP endpoint accepts trickle
        // ICE but we keep this simple and reliable; a future revision
        // can add the PATCH-based trickle flow if first-frame latency
        // becomes the bottleneck.
        await waitForIceComplete(pc);

        if (cancelled) {
          releaseConnection();
          return;
        }

        const offerSdp = pc.localDescription?.sdp ?? '';
        let answerSdp: string;
        if (sessionMode.kind === 'central') {
          const result = await exchangeCentralWhepOffer(cameraId, {
            offerSdp,
            bearerToken: await requireAccessToken(getAccessToken),
            signal: abortController.signal,
          });
          sessionResource = { kind: 'central', handle: result.handle };
          answerSdp = result.answerSdp;
        } else {
          const result = await exchangeWhepOffer(sessionMode.config.url, {
            offerSdp,
            signal: abortController.signal,
          });
          sessionResource = result.resourceUrl ? { kind: 'direct', url: result.resourceUrl } : null;
          answerSdp = result.answerSdp;
        }
        if (cancelled) {
          releaseConnection();
          return;
        }

        await pc.setRemoteDescription({
          type: 'answer',
          sdp: answerSdp,
        });
        if (!cancelled && video.srcObject) {
          armFirstFrameCallback();
          startPlayback();
        }
      } catch (err) {
        if (cancelled || abortController.signal.aborted) return;
        if (err instanceof WhepError) {
          // WhepError.kind is 'sdp' | 'network' | 'config'; map config
          // to local 'config' bucket (shouldn't happen here — we
          // validate the URL earlier).
          setTerminalError(err.kind === 'config' ? 'config' : err.kind, err.message);
        } else if (err instanceof Error) {
          // setRemoteDescription failures land here.
          setTerminalError('stream', err.message);
        } else {
          setTerminalError('stream', String(err));
        }
      }
    };

    if (connectDelay > 0) {
      handshakeTimer = setTimeout(() => {
        handshakeTimer = null;
        void handshake();
      }, connectDelay);
    } else {
      void handshake();
    }

    return (): void => {
      cancelled = true;
      video.removeEventListener('playing', markPlayingFromMediaEvent);
      video.removeEventListener('loadeddata', markPlayingFromMediaEvent);
      video.removeEventListener('timeupdate', markPlayingFromMediaEvent);
      video.removeEventListener('waiting', markFrameStalled);
      video.removeEventListener('stalled', markFrameStalled);
      video.removeEventListener('suspend', markFrameStalled);
      releaseConnection();
    };
  }, [
    autoplay,
    cameraId,
    connectDelay,
    getAccessToken,
    receiverJitterBufferTargetMs,
    retryToken,
    sessionMode,
  ]);

  const handleRetry = React.useCallback(() => {
    setRetryToken((n) => n + 1);
  }, []);
  const transportStatus = resolveWhepTransportStatus(state, frameHealth);
  React.useEffect(() => {
    onTransportStatusChange?.(transportStatus);
  }, [transportStatus, onTransportStatusChange]);
  React.useEffect(() => {
    if (state === 'error') {
      onUnavailable?.();
      return;
    }
    if (state !== 'playing' || frameHealth !== 'stalled' || !onUnavailable) return;
    const timer = setTimeout(onUnavailable, FRAME_STALL_AFTER_MS);
    return () => clearTimeout(timer);
  }, [state, frameHealth, onUnavailable]);

  return (
    <div
      className={cn(
        'relative isolate min-w-0 max-w-full overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-graphite-950)]',
        className,
      )}
      data-testid="whep-video-player"
      data-state={state}
      data-frame-health={frameHealth}
      data-error-kind={state === 'error' ? errorKind : undefined}
      data-camera-id={cameraId}
      data-stream-variant={streamVariant}
      data-session-mode={sessionMode?.kind}
      data-stream-segment={
        sessionMode?.kind === 'direct' ? sessionMode.config.streamSegment : undefined
      }
      data-jitter-buffer-target-ms={receiverJitterBufferTargetMs ?? undefined}
    >
      <video
        ref={handleVideoRef}
        aria-label={cameraName}
        className="block aspect-video h-full min-w-0 max-w-full w-full bg-[var(--color-graphite-950)]"
        muted={muted}
        autoPlay={autoplay}
        playsInline
      />
      {/* Overlay slot — detection bounding boxes, latency chip, etc. */}
      {state === 'playing' && frameHealth === 'flowing' && children ? (
        <div
          className="pointer-events-none absolute inset-0"
          data-testid="whep-video-player-overlay-slot"
        >
          {children}
        </div>
      ) : null}
      {state === 'connecting' && (
        <VideoStateOverlay
          tone="connecting"
          label={tr('video.connecting')}
          testId="whep-video-player-connecting"
        />
      )}
      {state === 'error' && !onUnavailable && (
        <VideoStateOverlay
          tone="error"
          testId="whep-video-player-unavailable"
          label={tr('video.unavailable')}
          detail={
            errorKind === 'config'
              ? tr('video.unavailableConfig')
              : errorKind === 'network'
                ? tr('video.networkError')
                : null
          }
          {...(errorKind === 'config'
            ? {}
            : { onRetry: handleRetry, retryLabel: tr('video.retry') })}
        />
      )}
      {renderTransportStatus ? (
        <div
          className="pointer-events-none absolute end-2 top-9 z-20"
          data-testid="whep-transport-status"
          data-transport-status={transportStatus}
        >
          {renderTransportStatus(transportStatus)}
        </div>
      ) : null}
    </div>
  );
}

export function resolveWhepTransportStatus(
  state: PlayerState,
  frameHealth: FrameHealth,
): WhepTransportStatus {
  if (state === 'error') return 'offline';
  if (state === 'connecting') return 'connecting';
  if (frameHealth === 'flowing') return 'live';
  return frameHealth === 'stalled' ? 'offline' : 'connecting';
}

/**
 * Wait for ICE gathering to finish, with a short soft ceiling that lets
 * the offer ship even if a STUN server is unreachable. MediaMTX accepts
 * an SDP without a complete candidate set; the missing candidates just
 * delay first-frame slightly.
 *
 * NOTE: this ICE-gathering ceiling is *independent* of the 1500ms
 * `DETECTION_TTL_MS` over in `use-detection-overlay.ts`. Both happen to
 * be timing windows but were derived from different reasoning (3× detection
 * cadence vs. WHEP startup wait). Do NOT consolidate them into a shared
 * constant — that would couple two unrelated timing decisions.
 */
function waitForIceComplete(pc: RTCPeerConnection): Promise<void> {
  return new Promise<void>((resolve) => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    // Keep live tiles from sitting on a black "Connecting" phase while ICE
    // completion lags. The staging WHEP path is L3-reachable and uses no STUN,
    // so waiting longer here only hurts first-frame latency.
    const timer = setTimeout(resolve, ICE_GATHERING_SOFT_TIMEOUT_MS);
    const onChange = (): void => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timer);
        pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', onChange);
  });
}

function applyReceiverJitterBufferTarget(
  receiver: RTCRtpReceiver | null,
  targetMs: number | null,
): void {
  if (receiver === null || targetMs === null || !('jitterBufferTarget' in receiver)) return;
  try {
    Reflect.set(receiver, 'jitterBufferTarget', targetMs);
  } catch {
    // Older implementations may expose the experimental property but reject it.
  }
}
